import React, { useMemo, useState } from "react";
import type { Idea, ReelTemplate, ReelTemplateKind } from "../api";
import { audienceForIdea, audienceLabel } from "../templates";
import { getReleaseBadge } from "../lib/release-badge";

type Audience = "all" | "b2c" | "b2b" | "affiliate" | "brand";
type Funnel = "all" | "awareness" | "interest" | "decision" | "action";
type Mode = "templates" | "ideas";
type TplKindFilter = "all" | ReelTemplateKind;
type SortMode = "newest" | "oldest" | "default";

type Props = {
  ideas: Idea[];
  templates: ReelTemplate[];
  onPick: (idea: Idea) => void;
  onLoadReel: (idea: Idea) => void;
  onLoadTemplate: (templateId: string) => void;
  onEmptyReel: () => void;
};

const KIND_LABEL: Record<ReelTemplateKind, string> = {
  reel: "Reel",
  story: "Story",
  carousel: "Carousel",
};

const KIND_ICON: Record<ReelTemplateKind, string> = {
  reel: "🎬",
  story: "✨",
  carousel: "🎠",
};

const FORMAT_LABEL: Record<string, string> = {
  "9:16": "9 : 16",
  "1:1": "1 : 1",
  "16:9": "16 : 9",
};

export const Gallery: React.FC<Props> = ({
  ideas,
  templates,
  onPick,
  onLoadReel,
  onLoadTemplate,
  onEmptyReel,
}) => {
  const [mode, setMode] = useState<Mode>("templates");
  const [audience, setAudience] = useState<Audience>("all");
  const [funnel, setFunnel] = useState<Funnel>("all");
  const [showOnlyAllyver, setShowOnlyAllyver] = useState<boolean>(true);
  const [tplKind, setTplKind] = useState<TplKindFilter>("all");
  const [sort, setSort] = useState<SortMode>("newest");
  const [onlyNew, setOnlyNew] = useState<boolean>(false);

  const sortByDate = <T extends { createdAt?: string }>(items: T[]): T[] => {
    if (sort === "default") return items;
    const arr = [...items];
    arr.sort((a, b) => {
      const ka = getReleaseBadge(a).sortKey;
      const kb = getReleaseBadge(b).sortKey;
      return sort === "newest" ? kb - ka : ka - kb;
    });
    return arr;
  };

  const filteredIdeas = useMemo(() => {
    const list = ideas.filter((i) => {
      if (showOnlyAllyver && !i.id.startsWith("ally_")) return false;
      if (funnel !== "all" && i.funnel !== funnel) return false;
      if (audience !== "all" && i.id.startsWith("ally_") && audienceForIdea(i.id) !== audience) return false;
      if (audience !== "all" && !i.id.startsWith("ally_") && audience !== "brand") return false;
      if (onlyNew && !getReleaseBadge(i).isNew) return false;
      return true;
    });
    return sortByDate(list);
  }, [ideas, audience, funnel, showOnlyAllyver, sort, onlyNew]);

  const filteredTemplates = useMemo(() => {
    const list = templates.filter((t) => {
      if (tplKind !== "all" && t.kind !== tplKind) return false;
      if (audience !== "all" && t.audience !== audience) return false;
      if (onlyNew && !getReleaseBadge(t).isNew) return false;
      return true;
    });
    return sortByDate(list);
  }, [templates, tplKind, audience, sort, onlyNew]);

  return (
    <section>
      <div className="section-title">
        <h2>Galerie</h2>
        <span className="hint">
          {mode === "templates"
            ? `${filteredTemplates.length} fertige Vorlagen — Scenes + Overlays + Format vorkonfiguriert`
            : `${filteredIdeas.length} Ideen · "Reel laden" für 15s-Preset · "Leeres Reel" für freien Composer`}
        </span>
        <button className="btn ghost" style={{ marginLeft: "auto" }} onClick={onEmptyReel}>
          Leeres Reel öffnen
        </button>
      </div>

      <div className="filter-bar" style={{ marginBottom: 8 }}>
        <button
          className={`chip ${mode === "templates" ? "active" : ""}`}
          onClick={() => setMode("templates")}
          title="Komplette Vorlagen mit Overlays + Format"
        >
          ✨ Vorlagen
        </button>
        <button
          className={`chip ${mode === "ideas" ? "active" : ""}`}
          onClick={() => setMode("ideas")}
          title="Inspirationspool an Hook-Ideen"
        >
          💡 Ideen
        </button>
      </div>

      <div className="gallery-sort-row">
        <span>Sortieren:</span>
        <button
          className={`chip ${sort === "newest" ? "active" : ""}`}
          onClick={() => setSort("newest")}
          title="Neueste zuerst"
        >
          Neueste
        </button>
        <button
          className={`chip ${sort === "oldest" ? "active" : ""}`}
          onClick={() => setSort("oldest")}
          title="Älteste zuerst"
        >
          Älteste
        </button>
        <button
          className={`chip ${sort === "default" ? "active" : ""}`}
          onClick={() => setSort("default")}
          title="Reihenfolge aus der Datei"
        >
          Standard
        </button>
        <span style={{ width: 1, background: "rgba(255,255,255,0.1)", margin: "0 4px", alignSelf: "stretch" }} />
        <button
          className={`chip ${onlyNew ? "active" : ""}`}
          onClick={() => setOnlyNew((v) => !v)}
          title="Nur Items zeigen, die in den letzten 14 Tagen hinzugekommen sind"
        >
          ✦ Nur NEU
        </button>
      </div>

      {mode === "templates" ? (
        <>
          <div className="filter-bar">
            {(["all", "reel", "story", "carousel"] as TplKindFilter[]).map((k) => (
              <button
                key={k}
                className={`chip ${tplKind === k ? "active" : ""}`}
                onClick={() => setTplKind(k)}
              >
                {k === "all"
                  ? "Alle Formate"
                  : `${KIND_ICON[k as ReelTemplateKind]} ${KIND_LABEL[k as ReelTemplateKind]}`}
              </button>
            ))}
            <span style={{ width: 1, background: "rgba(255,255,255,0.1)", margin: "0 4px" }} />
            {(["all", "b2c", "b2b", "affiliate", "brand"] as Audience[]).map((a) => (
              <button
                key={a}
                className={`chip ${audience === a ? "active" : ""}`}
                onClick={() => setAudience(a)}
              >
                {a === "all" ? "Alle Zielgruppen" : audienceLabel(a as Exclude<Audience, "all">)}
              </button>
            ))}
          </div>

          {filteredTemplates.length === 0 ? (
            <div className="empty">Keine Vorlagen mit diesen Filtern.</div>
          ) : (
            <div className="idea-grid">
              {filteredTemplates.map((t) => {
                const badge = getReleaseBadge(t);
                return (
                <article key={t.id} className={`idea-card tpl-card tpl-${t.kind}`}>
                  <div className="release-stamp release-stamp-corner">
                    {badge.isNew && <span className="release-pill-new">NEU</span>}
                    {badge.releaseLabel && !badge.isNew && (
                      <span className="release-pill-drop">{badge.releaseLabel}</span>
                    )}
                    {badge.dateLabel && (
                      <span className="release-date" title={`Hinzugefügt am ${badge.dateLabel}`}>
                        {badge.relativeLabel ?? badge.dateLabel}
                      </span>
                    )}
                  </div>
                  <span className={`audience tpl-kind ${t.kind}`}>
                    {KIND_ICON[t.kind]} {KIND_LABEL[t.kind]} · {FORMAT_LABEL[t.format] ?? t.format}
                  </span>
                  <h3>{t.title}</h3>
                  <p className="payoff">{t.tagline ?? t.description}</p>
                  <div className="meta">
                    <span>{t.scenes.length} Scenes · {t.overlays.length} Overlays</span>
                    <span className="stars">{Math.round(t.durationSec)}s</span>
                  </div>
                  {t.tags.length > 0 ? (
                    <div className="tpl-tags">
                      {t.tags.map((tag) => (
                        <span key={tag} className="tpl-tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <div className="card-actions">
                    <button
                      className="btn primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        onLoadTemplate(t.id);
                      }}
                    >
                      Vorlage laden
                    </button>
                  </div>
                </article>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="filter-bar">
            <button
              className={`chip ${showOnlyAllyver ? "active" : ""}`}
              onClick={() => setShowOnlyAllyver((v) => !v)}
            >
              Nur Allyver
            </button>
            <span style={{ width: 1, background: "rgba(255,255,255,0.1)", margin: "0 4px" }} />
            {(["all", "b2c", "b2b", "affiliate", "brand"] as Audience[]).map((a) => (
              <button
                key={a}
                className={`chip ${audience === a ? "active" : ""}`}
                onClick={() => setAudience(a)}
              >
                {a === "all" ? "Alle Zielgruppen" : audienceLabel(a as Exclude<Audience, "all">)}
              </button>
            ))}
            <span style={{ width: 1, background: "rgba(255,255,255,0.1)", margin: "0 4px" }} />
            {(["all", "awareness", "interest", "decision", "action"] as Funnel[]).map((f) => (
              <button
                key={f}
                className={`chip ${funnel === f ? "active" : ""}`}
                onClick={() => setFunnel(f)}
              >
                {f === "all" ? "Alle Funnel" : f}
              </button>
            ))}
          </div>

          {filteredIdeas.length === 0 ? (
            <div className="empty">Keine Ideen mit diesen Filtern. Setz die Filter zurück oder schalte "Nur Allyver" aus.</div>
          ) : (
            <div className="idea-grid">
              {filteredIdeas.map((idea) => {
                const aud = idea.id.startsWith("ally_") ? audienceForIdea(idea.id) : "brand";
                const badge = getReleaseBadge(idea);
                return (
                  <article key={idea.id} className="idea-card">
                    <div className="release-stamp release-stamp-corner">
                      {badge.isNew && <span className="release-pill-new">NEU</span>}
                      {badge.releaseLabel && !badge.isNew && (
                        <span className="release-pill-drop">{badge.releaseLabel}</span>
                      )}
                      {badge.dateLabel && (
                        <span className="release-date" title={`Hinzugefügt am ${badge.dateLabel}`}>
                          {badge.relativeLabel ?? badge.dateLabel}
                        </span>
                      )}
                    </div>
                    <span className={`audience ${aud}`}>{audienceLabel(aud)} · {idea.funnel}</span>
                    <h3>{idea.title}</h3>
                    <p className="payoff">{idea.payoff}</p>
                    <div className="meta">
                      <span>{idea.template}</span>
                      <span className="stars">{"★".repeat(idea.impactScore)}</span>
                    </div>
                    <div className="card-actions">
                      <button
                        className="btn primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          onLoadReel(idea);
                        }}
                      >
                        Reel laden (15s)
                      </button>
                      <button
                        className="btn ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          onPick(idea);
                        }}
                        title="Klassischer Editor (Single-Template)"
                      >
                        Editor (legacy)
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </>
      )}
    </section>
  );
};
